import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tag, Plus, Edit, Trash } from 'lucide-react';
import { WineTag } from '../../components/WineTags';

const TagManagementTab = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTagModal, setShowTagModal] = useState(false);
  const [currentTag, setCurrentTag] = useState({
    name: '',
    color: 'secondary',
    category: 'Process',
    description: '',
    systemGenerated: false
  });
  const [isEditing, setIsEditing] = useState(false);
  
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axios.get('/api/wine-tags');
        setTags(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tags:', error);
        setError('Failed to load tags');
        setLoading(false);
      }
    };
    
    fetchTags();
  }, []);
  
  const handleCreateTag = () => {
    setCurrentTag({
      name: '',
      color: 'secondary',
      category: 'Process',
      description: '',
      systemGenerated: false
    });
    setIsEditing(false);
    setShowTagModal(true);
  };
  
  const handleEditTag = (tag) => {
    setCurrentTag(tag);
    setIsEditing(true);
    setShowTagModal(true);
  };
  
  const handleDeleteTag = async (tagID) => {
    if (window.confirm('Are you sure you want to delete this tag? This will remove it from all wines.')) {
      try {
        await axios.delete(`/api/wine-tags/${tagID}`);
        setTags(tags.filter(tag => tag.tagID !== tagID));
      } catch (error) {
        console.error('Error deleting tag:', error);
        setError('Failed to delete tag');
      }
    }
  };
  
  const handleSaveTag = async () => {
    try {
      if (isEditing) {
        const response = await axios.put(`/api/wine-tags/${currentTag.tagID}`, currentTag);
        setTags(tags.map(tag => tag.tagID === currentTag.tagID ? response.data : tag));
      } else {
        const response = await axios.post('/api/wine-tags', currentTag);
        setTags([...tags, response.data]);
      }
      setShowTagModal(false);
    } catch (error) {
      console.error('Error saving tag:', error);
      setError('Failed to save tag');
    }
  };
  
  if (loading) return <div className="spinner-border text-primary" role="status"></div>;
  
  return (
    <div>
      <h5 className="mb-3"><Tag className="me-2" />Wine Tag Management</h5>
      
      {error && <div className="alert alert-danger">{error}</div>}
      
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Wine Tags</h5>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleCreateTag}
          >
            <Plus className="me-1" />
            New Tag
          </button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tags.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">No tags defined yet</td>
                  </tr>
                ) : (
                  tags.map(tag => (
                    <tr key={tag.tagID}>
                      <td>
                        <WineTag tag={tag} />
                      </td>
                      <td>{tag.category}</td>
                      <td>{tag.description}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleEditTag(tag)}
                            disabled={tag.systemGenerated}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDeleteTag(tag.tagID)}
                            disabled={tag.systemGenerated}
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Tag Modal */}
      {showTagModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {isEditing ? 'Edit Tag' : 'Create New Tag'}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowTagModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Tag Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={currentTag.name}
                      onChange={(e) => setCurrentTag({...currentTag, name: e.target.value})}
                      disabled={currentTag.systemGenerated}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Color</label>
                    <select 
                      className="form-select"
                      value={currentTag.color}
                      onChange={(e) => setCurrentTag({...currentTag, color: e.target.value})}
                      disabled={currentTag.systemGenerated}
                    >
                      <option value="primary">Blue</option>
                      <option value="secondary">Gray</option>
                      <option value="success">Green</option>
                      <option value="danger">Red</option>
                      <option value="warning">Yellow</option>
                      <option value="info">Light Blue</option>
                      <option value="dark">Black</option>
                    </select>
                    <div className="mt-2">
                      <span className={`badge bg-${currentTag.color}`}>Preview</span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Category</label>
                    <select 
                      className="form-select"
                      value={currentTag.category}
                      onChange={(e) => setCurrentTag({...currentTag, category: e.target.value})}
                      disabled={currentTag.systemGenerated}
                    >
                      <option value="Process">Process</option>
                      <option value="Quality">Quality</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Status">Status</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea 
                      className="form-control" 
                      rows="2"
                      value={currentTag.description}
                      onChange={(e) => setCurrentTag({...currentTag, description: e.target.value})}
                      disabled={currentTag.systemGenerated}
                    ></textarea>
                  </div>
                  {currentTag.systemGenerated && (
                    <div className="alert alert-info">
                      This is a system-generated tag and cannot be modified.
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowTagModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleSaveTag}
                    disabled={!currentTag.name || currentTag.systemGenerated}
                  >
                    {isEditing ? 'Update' : 'Create'} Tag
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TagManagementTab;